import { cloneDeep, has, isEmpty, memoize } from 'lodash'
import { computed, nextTick, ref, toValue, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useCreateWorkItem } from '@/utils/azure-devops-helpers/work-item'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { OKR_ELEMENT_ENTITY_KEYS, OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { DEFAULT_RESULT_TYPE_OPTIONS } from '@/utils/metrics-helpers'
import { isWorkItem, OBJECTIVE_TYPES, objectiveIsJiraTask } from '@/utils/objective-types'
import {
  currentUserCanUpdateObjective,
  getMetricLevelsForAdd,
  getObjectiveLevelsForCreate,
  isOkrElementClosed as checkIsOkrElementClosed,
  OKR_ELEMENT_FORM_MENU_ACTIONS,
  OKR_FORM_VIEWS,
  useOkrElementActionsMenuItems
} from '@/utils/objectives'
import { getPortalName } from '@/utils/okr-breadcrumbs'
import {
  AZURE_WORK_ITEM_AUTO_GRADE_OPTION,
  DEFAULT_MILESTONES_SETTINGS,
  EMPTY_MILESTONES_SETTINGS,
  GRADE_MODES,
  isBinaryProgress,
  JIRA_ISSUE_AUTO_GRADE_OPTION
} from '@/utils/okr-element-values'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import { FIELDS_COUNT } from '@/components/form/key-result/result-type-settings'

export const useElementTypes = () => {
  const store = useStore()

  const getElementTypeIdByLevelId = ({ levelId = null } = {}) => {
    const level = store.getters['objectives/getLevelItem'](OKR_LEVEL_ENTITY_KEYS.ID, levelId) || {
      [OKR_LEVEL_ENTITY_KEYS.TYPE_ID]: OBJECTIVE_TYPES.PERSONAL
    }

    return level[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]
  }

  return {
    getElementTypeIdByLevelId
  }
}

export const useProgressOptions = ({ formModel = {}, isEdit = false } = {}) => {
  const store = useStore()
  const resolvedIsEdit = toValue(isEdit)
  const { t } = useI18n()

  const levelOptions = computed(() => {
    return store.getters['objectives/getLevelItem'](
      OKR_LEVEL_ENTITY_KEYS.ID,
      toValue(formModel).levelId,
      {
        [OKR_ELEMENT_KEYS.WORKSPACE_ID]: toValue(formModel)[OKR_ELEMENT_KEYS.WORKSPACE_ID],
        [OKR_ELEMENT_KEYS.TYPE_ID]: toValue(formModel)[OKR_ELEMENT_KEYS.TYPE_ID]
      }
    )
  })

  const isTask = computed(() => objectiveIsJiraTask(levelOptions.value))
  const _isWorkItem = computed(() => isWorkItem(levelOptions.value))

  const getProgressOptions = computed(() => {
    const allAvailableProgressOptions = [
      ...(levelOptions.value[OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS] || [])
    ]

    let progressOptionsWithConflicts = []

    const { fieldTypeId: currentSelectedFieldTypeId } = toValue(formModel)

    if (
      !isTask.value &&
      !_isWorkItem.value &&
      resolvedIsEdit &&
      !allAvailableProgressOptions.includes(currentSelectedFieldTypeId)
    ) {
      progressOptionsWithConflicts.push(currentSelectedFieldTypeId)
    }

    const filteredOptions = DEFAULT_RESULT_TYPE_OPTIONS.filter(option => {
      return (
        !option.deprecated &&
        !option.unavailable &&
        [...allAvailableProgressOptions, ...progressOptionsWithConflicts].includes(option.value)
      )
    }).map(option => {
      const isConflict = progressOptionsWithConflicts.includes(option.value)
      return {
        ...option,
        icon: isConflict ? 'info-error' : option.icon,
        tooltipSettings: {
          placement: 'top',
          content: isConflict ? t('okr_element.conflicts.progress_option') : null,
          theme: `${DROP_LIST_THEMES.COMMON_TOOLTIP_THEMES} ${DROP_LIST_THEMES.TEXT_CENTER}`
        }
      }
    })

    if (isTask.value || _isWorkItem.value) {
      const option = isTask.value ? JIRA_ISSUE_AUTO_GRADE_OPTION : AZURE_WORK_ITEM_AUTO_GRADE_OPTION

      return [{ ...option }, ...filteredOptions]
    }

    return filteredOptions
  })

  return {
    getProgressOptions
  }
}

export const useParentElement = ({ formModel = {}, isEdit = false } = {}) => {
  const store = useStore()
  const resolvedIsEdit = toValue(isEdit)

  const levelOptions = computed(() =>
    store.getters['objectives/getLevelItem'](OKR_LEVEL_ENTITY_KEYS.ID, toValue(formModel).levelId)
  )

  const allowWithoutParent = computed(
    () => levelOptions.value[OKR_LEVEL_ENTITY_KEYS.ALLOW_WITHOUT_PARENT] || false
  )

  const showParentRequiredErrorOnCreate = ref(false)

  const removeParentRequiredOnCreateError = () => {
    if (showParentRequiredErrorOnCreate.value) {
      showParentRequiredErrorOnCreate.value = false
    }
  }

  const validateParentElement = () => {
    const { parentId } = toValue(formModel)

    if (allowWithoutParent.value) {
      removeParentRequiredOnCreateError()
      return {
        isParentValidationPassed: true
      }
    }

    showParentRequiredErrorOnCreate.value = !parentId

    return {
      isParentValidationPassed: resolvedIsEdit || !showParentRequiredErrorOnCreate.value // always pass validation on edit (only display error but don't block saving)
    }
  }

  watch(levelOptions, () => {
    removeParentRequiredOnCreateError()
  })

  const isShowParentRequiredError = computed(() => {
    if (resolvedIsEdit) {
      const { parentId } = toValue(formModel)

      return !allowWithoutParent.value && !parentId
    }

    return showParentRequiredErrorOnCreate.value
  })

  return {
    isShowUnlinkParentButton: allowWithoutParent,
    isShowParentRequiredError,
    validateParentElement
  }
}

export const useCreateNestedElementDropdownItems = ({ modelValue = {} } = {}) => {
  const { workspaceId } = useElementForm({
    modelValue,
    formView: 'unknown'
  })

  const resolvedWorkspaceId = toValue(workspaceId)

  const store = useStore()

  const currentWorkspaceLevelsByElementTypes = computed(() => {
    const allWorkspaceLevels = store.getters['objectives/getLevelsByWorkspaceId'](
      Number(resolvedWorkspaceId)
    )

    return {
      objectiveLevels: getObjectiveLevelsForCreate(allWorkspaceLevels),
      metricLevels: getMetricLevelsForAdd(allWorkspaceLevels)
    }
  })

  const { addActions } = useOkrElementActionsMenuItems(
    () => toValue(modelValue),
    () => currentWorkspaceLevelsByElementTypes.value.objectiveLevels,
    () => currentWorkspaceLevelsByElementTypes.value.metricLevels
  )

  const createNestedElementDropdownItems = computed(() => addActions.value)

  const { isTryingCreateWorkItemById } = useCreateWorkItem()

  const getPayloadForHandleAction = memoize(
    ({ actionId = null } = {}) => {
      const isTaskAction = [
        OKR_ELEMENT_FORM_MENU_ACTIONS.LINK_TASK,
        OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_JIRA_ISSUE
      ].includes(actionId)

      if (isTaskAction) {
        return {
          isTaskAction
        }
      }

      const isWorkItemAction =
        [
          OKR_ELEMENT_FORM_MENU_ACTIONS.LINK_WORK_ITEM,
          OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_WORK_ITEM
        ].includes(actionId) || isTryingCreateWorkItemById(actionId)

      if (isWorkItemAction) {
        return {
          isWorkItemAction
        }
      }

      if (
        [
          OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_OBJECTIVE,
          OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_KR
        ].includes(actionId)
      ) {
        const isObjectiveAction = actionId === OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_OBJECTIVE

        const targetLevels = isObjectiveAction
          ? currentWorkspaceLevelsByElementTypes.value.objectiveLevels
          : currentWorkspaceLevelsByElementTypes.value.metricLevels

        const [firstLevel] = targetLevels

        return {
          ...cloneDeep(firstLevel),
          levelId: firstLevel[OKR_LEVEL_ENTITY_KEYS.ID]
        }
      }

      const selectedLevel = [
        ...currentWorkspaceLevelsByElementTypes.value.objectiveLevels,
        ...currentWorkspaceLevelsByElementTypes.value.metricLevels
      ].find(item => item.id === actionId) || {
        [OKR_ELEMENT_KEYS.LEVEL_ID]: null,
        [OKR_ELEMENT_KEYS.TYPE_ID]: OBJECTIVE_TYPES.PERSONAL
      }

      return {
        ...cloneDeep(selectedLevel),
        levelId: selectedLevel[OKR_LEVEL_ENTITY_KEYS.ID]
      }
    },
    ({ actionId }) => `menu-item-${actionId}`
  )

  return {
    createNestedElementDropdownItems,
    getPayloadForHandleAction
  }
}

export const useElementForm = ({
  modelValue = {},
  formModel = {},
  formView = OKR_FORM_VIEWS.OBJECTIVE
} = {}) => {
  const route = useRoute()

  const resolvedModelValue = computed(() => toValue(modelValue))

  const workspaceId = computed(() => {
    if (
      resolvedModelValue.value &&
      resolvedModelValue.value.workspaceId &&
      resolvedModelValue.value.workspaceId !== -1
    ) {
      return resolvedModelValue.value.workspaceId
    }

    return route.params.workspaceId
  })

  const isEdit = computed(() => {
    return Boolean(resolvedModelValue.value && resolvedModelValue.value[OKR_ELEMENT_ENTITY_KEYS.ID])
  })

  const initialDataAreLoaded = ref(false)

  const editable = computed(() => {
    if (!isEdit.value) return false

    return currentUserCanUpdateObjective(resolvedModelValue.value)
  })

  const isOkrElementClosed = computed(() => {
    if (formView === OKR_FORM_VIEWS.LINK_JIRA_ISSUE) return false
    if (formView === OKR_FORM_VIEWS.LINK_WORK_ITEM) return false
    return checkIsOkrElementClosed(resolvedModelValue.value)
  })

  const disableOnEdit = computed(() => {
    if (!isEdit.value) return false
    if (!initialDataAreLoaded.value) return true
    return (isEdit.value && !editable.value) || isOkrElementClosed.value
  })

  const showWeightsModalTrigger = computed(() => {
    // return this.isEdit && this.editable && this.childKrs.length > 1
    return isEdit.value && editable.value
  })

  const selectSkeletonLoadersOpacity = computed(() => {
    return disableOnEdit.value ? 0.9 : 0.5
  })

  const portalName = computed(() => getPortalName(formView, isEdit.value))

  const elementFormReferences = computed(() => {
    const postfix = isEdit.value ? 'edit' : 'create'

    const formViewNames = {
      [OKR_FORM_VIEWS.OBJECTIVE]: 'objective',
      [OKR_FORM_VIEWS.KR]: 'kr',
      [OKR_FORM_VIEWS.LINK_JIRA_ISSUE]: 'link-jira-issue',
      [OKR_FORM_VIEWS.LINK_WORK_ITEM]: 'link-work-item'
    }

    const formViewName = formViewNames[formView] || 'unknown'

    return {
      title: `${formViewName}-title:${postfix}`,
      activities: `${formViewName}-activities:${postfix}`,
      // postfix is not needed for createJiraIssueForm because jira issue can be created only for
      // already existing OKR elements.
      // A dynamic reference causes an error when the user tries to create a Jira issue
      // while creating an OKR element.
      // The reference changes to a new one, but `createJiraIssueFormReference`
      // remains linked to the old reference with the old name.
      createJiraIssueForm: `${formViewName}-createJiraIssueForm`,
      createWorkItemForm: `${formViewName}-createWorkItemForm`,
      milestones: `${formViewName}-milestones:${postfix}`
    }
  })

  const createJiraIssueFormReference = useTemplateRef(
    elementFormReferences.value.createJiraIssueForm
  )

  const createJiraIssue = payload => {
    createJiraIssueFormReference.value.create(payload)
  }

  const createWorkItemFormReference = useTemplateRef(elementFormReferences.value.createWorkItemForm)

  const startWorkItemCreation = (parentElement, workItemKindId) => {
    createWorkItemFormReference.value.create(parentElement, { workItemKindId })
  }

  const activitiesReference = useTemplateRef(elementFormReferences.value.activities)

  const updateActivities = () => {
    activitiesReference.value?.refreshData()
  }

  const titleReference = useTemplateRef(elementFormReferences.value.title)

  const setFocusOnTitle = () => {
    // nextTick and setTimeout(both!) are needed to make input always focused on modal window
    // opening because modal window(o-modal) has transition of opacity with 0.2s
    nextTick(() => {
      setTimeout(() => {
        titleReference.value && titleReference.value.focus()
      }, 200)
    })
  }

  const milestonesSettings = ref(cloneDeep(EMPTY_MILESTONES_SETTINGS))

  const milestonesReference = useTemplateRef(elementFormReferences.value.milestones)

  const requestDataUpdateFromMilestones = async ({ fieldTypeId = null } = {}) => {
    return await milestonesReference.value?.requestDataUpdate({ fieldTypeId })
  }

  const restoreToDefaultsMilestones = () => {
    milestonesSettings.value = cloneDeep(EMPTY_MILESTONES_SETTINGS)
    return milestonesReference.value?.restoreDefaults()
  }

  const forceUpdateMilestones = () => {
    milestonesSettings.value = cloneDeep(EMPTY_MILESTONES_SETTINGS)
    return milestonesReference.value?.forceUpdate()
  }

  const isResultTypeSettingsDisabled = computed(() => {
    if (formView === OKR_FORM_VIEWS.KR) {
      return resolvedModelValue.value && resolvedModelValue.value.existContributeChildren
    }

    return false
  })

  const saveMilestonesSettingsFromFormModel = () => {
    const milestonesRequiredKeys = [
      OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS,
      OKR_ELEMENT_KEYS.PROGRESS_AGGREGATION_TYPE_ID,
      OKR_ELEMENT_KEYS.GRANULATION_ID
    ]

    if (milestonesRequiredKeys.every(key => has(toValue(modelValue), key))) {
      const sourceKeys = [...milestonesRequiredKeys, OKR_ELEMENT_KEYS.PERIOD_AGGREGATION_TYPE_ID]

      const newSettings = {}

      sourceKeys.forEach(settingKey => {
        newSettings[settingKey] =
          toValue(modelValue)[settingKey] || DEFAULT_MILESTONES_SETTINGS[settingKey]
      })

      milestonesSettings.value = cloneDeep(newSettings)
    }
  }

  const isShowProgressLockMessage = computed(() => {
    if (formView === OKR_FORM_VIEWS.KR) {
      return (
        isEdit.value &&
        editable.value &&
        !isOkrElementClosed.value &&
        isResultTypeSettingsDisabled.value
      )
    }

    return false
  })

  const isBinary = computed(() => {
    if (formView !== OKR_FORM_VIEWS.OBJECTIVE) {
      return isBinaryProgress({ element: toValue(formModel) })
    }
    return false
  })

  const isAutoMode = computed(() => {
    if (formView === OKR_FORM_VIEWS.LINK_JIRA_ISSUE || formView === OKR_FORM_VIEWS.LINK_WORK_ITEM) {
      return toValue(formModel).gradeMode === GRADE_MODES.AUTO
    }

    return false
  })

  const resultTypeSettingsFieldsCount = computed(() => {
    if (isAutoMode.value) {
      return FIELDS_COUNT.ONE_FIELD
    }

    return isBinary.value ? FIELDS_COUNT.ONE_FIELD : FIELDS_COUNT.TWO_FIELDS

    //if (isBinary.value) {
    //  return FIELDS_COUNT.ONE_FIELD
    //}
    //
    //if (isEdit.value && !isBinary.value) {
    //  return FIELDS_COUNT.THREE_FIELDS
    //}
    //
    //return FIELDS_COUNT.TWO_FIELDS
  })

  return {
    workspaceId,
    isEdit,
    editable,
    isOkrElementClosed,
    initialDataAreLoaded,
    disableOnEdit,
    selectSkeletonLoadersOpacity,
    portalName,
    showWeightsModalTrigger,
    createJiraIssue,
    startWorkItemCreation,
    updateActivities,
    setFocusOnTitle,
    elementFormReferences,
    isShowProgressLockMessage,
    milestonesSettings,
    requestDataUpdateFromMilestones,
    restoreToDefaultsMilestones,
    forceUpdateMilestones,
    saveMilestonesSettingsFromFormModel,
    resultTypeSettingsFieldsCount,
    isAutoMode,
    isResultTypeSettingsDisabled
  }
}

export const ADD_NESTED_ELEMENT_EMIT_EVENTS = {
  OPEN_CHILD_OBJECTIVE: 'open-child-objective',
  OPEN_KR: 'open-kr',
  CREATE_NESTED_WHILE_CREATE_CURRENT: 'create-nested-while-create-current'
}

export const ELEMENT_PROGRESS_ADJUSTMENT_TABS = {
  CHART: {
    value: 'chart',
    label: 'element.progress_adjustment.progress'
  },
  SETUP: {
    value: 'setup',
    label: 'element.progress_adjustment.progress_setup'
  }
}

export const useElementProgressAdjustment = ({
  isEdit = false,
  isResultTypeSettingsDisabled = false
} = {}) => {
  const { t } = useI18n()

  const resolvedIsEdit = toValue(isEdit)
  const resolvedIsResultTypeSettingsDisabled = computed(() => toValue(isResultTypeSettingsDisabled))

  const activeTab = ref(null)

  const progressAdjustmentTabs = computed(() => {
    const result = []

    if (resolvedIsEdit) {
      result.push({ ...ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART })
    }

    if (!resolvedIsResultTypeSettingsDisabled.value) {
      result.push({ ...ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP })
    }

    return result.map(tab => {
      return {
        ...tab,
        label: t(tab.label)
      }
    })
  })

  const currentProgressAdjustmentTab = computed({
    get: () => {
      if (isEmpty(progressAdjustmentTabs.value)) return null
      return activeTab.value || progressAdjustmentTabs.value[0].value
    },
    set: value => {
      activeTab.value = value
    }
  })

  const isShowTabSwitcher = computed(() => {
    return progressAdjustmentTabs.value.length > 1
  })

  return {
    progressAdjustmentTabs,
    currentProgressAdjustmentTab,
    isShowTabSwitcher
  }
}
